import { useState } from "react";

import { Controller, FormProvider, useForm } from "react-hook-form";
import { useNavigate } from "react-router";

import { useMutation, useQueryClient } from "@tanstack/react-query";

import { zodResolver } from "@hookform/resolvers/zod";

import { Trash2Icon } from "lucide-react";

import { AlertDialog, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import { Field } from "@/components/ui/field";
import { Input } from "@/components/ui/input";

import { DeleteProjectSchema, deleteProjectSchema } from "@/features/projects/schemas";
import { deleteProject } from "@/features/projects/services/project";
import { Project } from "@/features/projects/types";
import Error from "@/shared/components/Error";
import { PermissionTooltip } from "@/shared/components/PermissionTooltip";
import { QUERY_KEYS } from "@/shared/lib/constants/queries";
import { AppError } from "@/shared/lib/utils/errors";

interface DeleteProjectFormProps {
  project: Project;
  disabled?: boolean;
}

export default function DeleteProjectForm({ project, disabled = false }: DeleteProjectFormProps) {
  const [open, setOpen] = useState(false);

  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const form = useForm<DeleteProjectSchema>({
    resolver: zodResolver(deleteProjectSchema),
    defaultValues: {
      id: project.id,
    },
  });

  const mutation = useMutation<undefined, AppError | Error, DeleteProjectSchema>({
    mutationFn: deleteProject,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.GET_MY_PROJECTS], exact: true });
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.GET_PROJECT_BY_ID, project.id], exact: true });
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.GET_MY_PERMISSIONS_IN_PROJECT, project.id], exact: true });
      navigate("/projects");
    },
  });

  async function onSubmit(values: DeleteProjectSchema) {
    mutation.mutate(values);
  }

  if (disabled) {
    return (
      <PermissionTooltip allowed={false} message="You don't have permission to delete this project.">
        <Button variant="destructive" size="sm" className="w-fit">
          <Trash2Icon />
          Delete project
        </Button>
      </PermissionTooltip>
    );
  }

  return (
    <AlertDialog
      open={open}
      onOpenChange={(newOpen) => {
        if (!mutation.isPending) {
          setOpen(newOpen);
        }
      }}
    >
      <AlertDialogTrigger asChild>
        <Button variant="destructive" size="sm" className="w-fit">
          <Trash2Icon />
          Delete project
        </Button>
      </AlertDialogTrigger>
      <AlertDialogContent>
        <FormProvider {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="flex flex-col gap-6">
            <AlertDialogHeader>
              <AlertDialogTitle>Delete &ldquo;{project.name}&rdquo;?</AlertDialogTitle>
              <AlertDialogDescription asChild>
                <p>
                  This will permanently delete the project and all{" "}
                  <strong>
                    {project.totalTicketCount} {project.totalTicketCount === 1 ? "ticket" : "tickets"} and {project.totalMemberCount} {project.totalMemberCount === 1 ? "member" : "members"}
                  </strong>
                  . This action <strong>cannot be undone</strong>.
                </p>
              </AlertDialogDescription>
            </AlertDialogHeader>
            {mutation.isError && <Error title="Unable to delete project" error={mutation.error} />}
            <Controller
              name="id"
              control={form.control}
              render={({ field, fieldState }) => (
                <Field data-invalid={fieldState.invalid}>
                  <Input type="hidden" {...field} disabled={mutation.isPending || mutation.isSuccess} aria-invalid={fieldState.invalid} />
                </Field>
              )}
            />
            <AlertDialogFooter>
              <AlertDialogCancel disabled={mutation.isPending || mutation.isSuccess}>Cancel</AlertDialogCancel>
              <Button type="submit" variant="destructive" className="disabled:opacity-40" disabled={mutation.isPending || mutation.isSuccess}>
                <Trash2Icon />
                {mutation.isPending || mutation.isSuccess ? "Deleting project..." : "Delete project"}
              </Button>
            </AlertDialogFooter>
          </form>
        </FormProvider>
      </AlertDialogContent>
    </AlertDialog>
  );
}
